import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchPersonaTimeMetric,
  type WorkbenchPersonaTimeResponse,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import type { ChatSeedInput } from './EmbeddedChatPanel';

const METRICS: { key: WorkbenchPersonaTimeMetric; label: string }[] = [
  { key: 'spend', label: 'Spend' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'ltv_revenue', label: 'LTV Revenue' },
  { key: 'cac', label: 'CAC' },
  { key: 'ltv_roas', label: 'LTV ROAS' },
];

const PALETTE: Record<string, string> = {
  default: '#14b8a6',
  Uncategorized: '#94a3b8',
};

const SERIES_PALETTE = ['#14b8a6', '#8b5cf6', '#ec4899', '#f59e0b', '#0ea5e9', '#84cc16'];

interface Props {
  onDrillDown: (seed: ChatSeedInput) => void;
}

export function PersonaTimeChart({ onDrillDown }: Props) {
  const { clientUid, dateRange } = useCreativeWorkbench();
  const [metric, setMetric] = useState<WorkbenchPersonaTimeMetric>('spend');
  const [alignedOnly, setAlignedOnly] = useState(false);
  const [data, setData] = useState<WorkbenchPersonaTimeResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getPersonaTimeSeries({
        clientUid,
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        metric,
        alignedOnly,
      })
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, dateRange.from, dateRange.to, metric, alignedOnly]);

  // Reshape into recharts-friendly rows: { date, persona1, persona2, ..., Uncategorized }
  const rows = useMemo(() => {
    if (!data) return [];
    return data.points.map((p) => {
      const row: Record<string, string | number> = { date: p.date };
      for (const persona of data.personas) {
        row[persona] = 0;
      }
      for (const entry of p.by) {
        row[entry.label] = entry.value;
      }
      return row;
    });
  }, [data]);

  const colorFor = (persona: string, idx: number) =>
    PALETTE[persona] ?? SERIES_PALETTE[idx % SERIES_PALETTE.length];

  const handleClick = (payload: any) => {
    const date = payload?.activeLabel;
    if (!date) return;
    onDrillDown({
      question: `Walk me through ${metric} on ${date}. What changed by persona?`,
      chart_axis: { series: metric, dimension: 'persona', anchor_date: date },
      date_range: { from: dateRange.from, to: dateRange.to },
    });
  };

  const totalsByPersona = useMemo(() => {
    if (!data) return [];
    const m = new Map<string, number>();
    for (const p of data.points) {
      for (const e of p.by) {
        m.set(e.label, (m.get(e.label) ?? 0) + e.value);
      }
    }
    return Array.from(m.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([persona, value]) => ({ persona, value }));
  }, [data]);

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-bold text-navy-900">
            Persona Performance Over Time
          </h2>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Stacked daily trend, attributed by persona — the lens that proves
            the model.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PillGroup
            options={METRICS.map((m) => ({ value: m.key, label: m.label }))}
            value={metric}
            onChange={(v) => setMetric(v as WorkbenchPersonaTimeMetric)}
          />
          <label className="flex items-center gap-1 text-[10px] font-mono text-slate-500 cursor-pointer">
            <input
              type="checkbox"
              checked={alignedOnly}
              onChange={(e) => setAlignedOnly(e.target.checked)}
              className="w-3 h-3 accent-teal-500"
            />
            Aligned only
          </label>
        </div>
      </header>

      <div className="h-72">
        {loading && !data && (
          <div className="text-xs text-slate-400 h-full flex items-center justify-center">
            Loading...
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-xs text-slate-400 h-full flex items-center justify-center">
            No persona-attributed performance in this window.
          </div>
        )}
        {rows.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} onClick={handleClick}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} />
              <YAxis stroke="#94a3b8" fontSize={10} />
              <Tooltip
                wrapperClassName="text-xs"
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                formatter={(value: number, name: string) => [
                  formatTooltipValue(value, metric),
                  name,
                ]}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {(data?.personas ?? []).map((persona, idx) => (
                <Area
                  key={persona}
                  type="monotone"
                  dataKey={persona}
                  stackId="persona"
                  stroke={colorFor(persona, idx)}
                  fill={colorFor(persona, idx)}
                  fillOpacity={persona === 'Uncategorized' ? 0.15 : 0.35}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {totalsByPersona.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
          {totalsByPersona.slice(0, 6).map((t, idx) => (
            <span
              key={t.persona}
              className="inline-flex items-center gap-1.5 text-[10px] font-mono text-slate-500"
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: colorFor(t.persona, idx) }}
              />
              {t.persona}
              <span className="text-navy-900 font-bold">
                {formatTooltipValue(t.value, metric)}
              </span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function formatTooltipValue(value: number, metric: WorkbenchPersonaTimeMetric): string {
  if (metric === 'spend' || metric === 'ltv_revenue' || metric === 'cac') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  }
  if (metric === 'ltv_roas') return `${value.toFixed(2)}x`;
  return value.toLocaleString();
}

function PillGroup<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex border border-slate-200 rounded-lg overflow-hidden">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={clsx(
            'px-2 py-1 text-[10px] font-mono uppercase transition-colors',
            value === opt.value
              ? 'bg-teal-500 text-white'
              : 'bg-white text-slate-500 hover:bg-slate-50',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
